import clsx from 'clsx';
import { ChevronDown } from 'mdi-material-ui';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Typography, Button } from '../../Elements';

// TODO assets should be app specific?
//import EditButton from '@/assets/edit_button.svg';

export type CollapsibleSectionContainerProps = {
	actionButton?: React.ReactElement | undefined | false;
	children: React.ReactElement;
	editLink?: string;
	isEditable: boolean;
	editIcon?: string;
	title: string;
	titleIcon?: React.ReactElement;
	isFullWidth?: boolean;
	expandedOnLoad?: boolean;
	classNames?: string;
	expandedClassNames?: string;
	dataTestId?: string;
	isTransparentBackground?: boolean;
	titleClassNames?: string;
	containerOffset?: number; // if the child content has negative margin this should match the value for a smoother animation
	showBorder?: boolean;
	setShowEdit?: React.Dispatch<React.SetStateAction<boolean>>;
	customButtons?: {
		expandedButton: React.ReactElement;
		expandedButtonClass: string;
		closedButton: React.ReactElement;
		closedButtonClass: string;
	};
};

export const CollapsibleSectionContainer = (props: CollapsibleSectionContainerProps) => {
	const {
		actionButton,
		children,
		editLink = '',
		isEditable,
		editIcon,
		title,
		titleIcon,
		isFullWidth = false,
		expandedOnLoad = true,
		classNames = '',
		expandedClassNames = '',
		dataTestId = '',
		isTransparentBackground = true,
		titleClassNames = '',
		containerOffset = 0,
		showBorder = false,
		setShowEdit,
		customButtons,
	} = props;
	const [isFirstRender, setIsFirstRender] = useState(true);
	const [isExpanded, setIsExpanded] = useState(expandedOnLoad);
	const heightHidden = '0px';
	const heightExpanded = 'auto';
	const initialHeight = expandedOnLoad ? heightExpanded : heightHidden;
	const [containerHeight, setContainerHeight] = useState(initialHeight);
	const contentDivRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// set to height of content to enable transition
		const offsetHeight = contentDivRef.current?.offsetHeight || 200;
		setContainerHeight(`${offsetHeight - containerOffset}px`);
		setContainerHeight(isFirstRender ? initialHeight : `${offsetHeight - containerOffset}px`);
		setIsFirstRender(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isExpanded]);

	useEffect(() => {
		// we can't transition to/from height auto but don't want to set a fixed height for the content because we'll lose responsiveness
		// this resets the height to either 0 or auto depending on if expanded or not. If auto then we wait for the transition to complete.
		const afterTransitionTime = 600; // wait for transition
		const afterRenderTime = 100; // start transition straight away, but wait for render
		const timeoutTime = isExpanded ? afterTransitionTime : afterRenderTime;
		// if expanded then we set to height auto otherwise set to height hidden
		const newHeight = isExpanded ? heightExpanded : heightHidden;
		setTimeout(() => {
			if (newHeight !== containerHeight) {
				setContainerHeight(newHeight);
			}
		}, timeoutTime);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerHeight]);

	return (
		<div
			className={clsx(
				'w-full',
				isTransparentBackground ? '' : 'bg-secondaryBackground p-6',
				showBorder ? 'border border-secondaryBorder rounded-md' : '',
				isExpanded ? expandedClassNames : '',
				classNames
			)}
			data-test-id={`${dataTestId}-collapsible-container`}
		>
			<div className='flex gap-4 items-center'>
				<div className='flex flex-row justify-between items-center w-full'>
					<div
						className={clsx(
							'flex flex-row justify-start items-center',
							isFullWidth ? 'w-full' : ''
						)}
					>
						<button
							type='button'
							onClick={() => {
								setIsExpanded(!isExpanded);
							}}
							className={`${isFullWidth ? 'pl-3' : ''}`}
							data-test-id='collapse-expand-toggle-button'
						>
							{titleIcon}
							<Typography
								variant='title4'
								className={`text-primaryTextDark mr-2 text-left ${titleClassNames} ${!!titleIcon ? 'ml-10' : ''}`}
							>
								{title}
							</Typography>
						</button>
						{isEditable &&
							(setShowEdit ? (
								<Button
									variant='icon'
									size='icon'
									onClick={() => {
										setShowEdit(true);
									}}
									data-test-id={`${dataTestId}-edit-link`}
								>
									{editIcon ? (
										<img src={editIcon} alt='edit' className='w-6 h-6' />
									) : (
										'Edit'
									)}
								</Button>
							) : (
								<Link
									className='flex flex-row justify-center items-center w-7 h-7 rounded-full bg-highlight text-base mr-2'
									to={editLink}
									data-test-id={`${dataTestId}-edit-link`}
								>
									{editIcon ? (
										<img src={editIcon} alt='edit' className='w-6 h-6' />
									) : (
										'Edit'
									)}
								</Link>
							))}
						<Button
							variant={
								customButtons ? (isExpanded ? 'secondary' : 'primary') : 'icon'
							}
							size={customButtons ? 'lg' : 'icon'}
							onClick={() => {
								setIsExpanded(!isExpanded);
							}}
							className={`${isFullWidth ? 'ml-auto mr-3' : ''}
								${customButtons ? (isExpanded ? customButtons.expandedButtonClass : customButtons.closedButtonClass) : 'w-[32px] h-[32px] !bg-highlight hover:!bg-highlightHover rounded-full'}
							}`}
						>
							{customButtons ? (
								isExpanded ? (
									<>{customButtons?.expandedButton}</>
								) : (
									<>{customButtons?.closedButton}</>
								)
							) : (
								<ChevronDown
									className={`!text-[24px] !transition-transform !ease-in-out !duration-500 ${
										isExpanded
											? 'text-primaryBrand transform rotate-180'
											: 'text-secondaryTextDark rotate-0'
									}`}
								/>
							)}
							<span className='sr-only'>{isExpanded ? 'Collapse' : 'Expand'}</span>
						</Button>
					</div>
					{actionButton}
				</div>
			</div>
			<div
				className={`transition-height duration-500 ease-linear ${
					isFullWidth ? 'px-3' : ''
				}`}
				style={{
					height: containerHeight,
					overflow: containerHeight === heightExpanded ? 'initial' : 'hidden',
				}}
			>
				<div className='mt-4' ref={contentDivRef}>
					{children}
				</div>
			</div>
		</div>
	);
};
